import { Localization } from "../types/types";
import { buildCanonicalUrl } from "../seo/site";

export type LocalePrefix = "fi" | "en";

export const DEFAULT_LOCALE_PREFIX: LocalePrefix = "fi";

export const isLocalePrefix = (value: string | undefined | null): value is LocalePrefix =>
  value === "fi" || value === "en";

export const localizationToPrefix = (language: Localization): LocalePrefix =>
  language === Localization.Finnish ? "fi" : "en";

export const prefixToLocalization = (prefix: LocalePrefix): Localization =>
  prefix === "fi" ? Localization.Finnish : Localization.English;

export const inferLanguage = (): Localization => {
  if (typeof navigator === "undefined") return Localization.English;

  const langs = navigator.languages.filter(Boolean).map((language) => language.toLowerCase());
  if (langs.some((language) => language.startsWith("fi"))) return Localization.Finnish;

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone.toLowerCase();
    if (tz.includes("helsinki")) return Localization.Finnish;
  } catch {
    // ignore
  }

  return Localization.English;
};

export const preferredLocalePrefix = (): LocalePrefix => localizationToPrefix(inferLanguage());

const normalizePath = (path: string): string => {
  if (!path || path === "/") return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

export const withLocalePrefix = (path: string, prefix: LocalePrefix): string => {
  const [rawPath, search = ""] = path.split("?");
  const normalized = normalizePath(rawPath);
  const localized = normalized === "/" ? `/${prefix}` : `/${prefix}${normalized}`;
  return search ? `${localized}?${search}` : localized;
};

export const splitLocalePath = (
  pathname: string,
): { prefix: LocalePrefix | null; path: string } => {
  const match = pathname.match(/^\/(fi|en)(?=\/|$)/);
  if (!match) {
    return { prefix: null, path: normalizePath(pathname) };
  }

  const prefix = match[1] as LocalePrefix;
  const remainder = pathname.slice(prefix.length + 1);
  return {
    prefix,
    path: normalizePath(remainder || "/"),
  };
};

export const swapLocalePrefix = (pathname: string, prefix: LocalePrefix): string => {
  const { path } = splitLocalePath(pathname);
  return withLocalePrefix(path, prefix);
};

export const isActiveInternalPath = (internalPath: string, pathname: string): boolean => {
  const { path } = splitLocalePath(pathname);
  if (internalPath === "/") return path === "/";
  return path === internalPath || path.startsWith(`${internalPath}/`);
};

export const buildHreflangLinks = (internalPath: string) => {
  const path = normalizePath(internalPath);
  return [
    { hreflang: "fi", href: buildCanonicalUrl(withLocalePrefix(path, "fi")) },
    { hreflang: "en", href: buildCanonicalUrl(withLocalePrefix(path, "en")) },
    {
      hreflang: "x-default",
      href: buildCanonicalUrl(withLocalePrefix(path, DEFAULT_LOCALE_PREFIX)),
    },
  ];
};
